import { useEffect, useRef, useState } from "react";
import { redirect, useActionData, useLocation, useNavigation, Form, type ActionFunctionArgs } from "react-router";
import { FieldSkeleton, Loading } from "../components/skeletons.jsx";
import { translate, useI18n } from "../i18n/index.js";
import { isApiError } from "../lib/api.js";
import { apiContext, invalidateSession } from "../lib/session.js";
import { rememberWorkspace } from "../lib/workspace.js";
import { Button, CenteredPage, ChevronLeftIcon, FormError, LinkButton, Skeleton, TextField, useToast } from "../ui/index.js";

/** A rejected field is named so the form can mark that input rather than the page. */
type ActionResult = { error: string; field?: "name" | "slug" };

/** Where the sidebar's "New Workspace" was clicked. Only an in-app workspace path counts. */
function backTarget(state: unknown): string | null {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === "string" && from.startsWith("/w/") ? from : null;
}

/** The URL a name suggests. A name with nothing a URL can hold (Chinese, say) gets `fallback`. */
export function slugFor(name: string, fallback: string): string {
  const slug = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48)
    .replace(/-+$/, "");
  return slug.length >= 2 ? slug : name.trim() ? fallback : "";
}

export async function createWorkspaceAction({ request, context }: ActionFunctionArgs) {
  const form = await request.formData();
  const name = String(form.get("name") ?? "").trim();
  const slug = String(form.get("slug") ?? "").trim();
  try {
    const ws = await context.get(apiContext).createWorkspace({ name, slug });
    rememberWorkspace(ws.slug);
    invalidateSession(); // the cached /api/me predates this workspace
    // A new workspace starts by connecting what it already knows (D-0022).
    return redirect(`/w/${ws.slug}/setup`);
  } catch (err) {
    // A taken slug is the user's to fix in this form, so it belongs on the field
    // — not on the error boundary, which would throw away what they typed.
    if (isApiError(err, "conflict")) return { error: translate("workspaceNew.slugTaken"), field: "slug" } satisfies ActionResult;
    if (isApiError(err, "validation_failed")) {
      const field = (err.details as { field?: string } | undefined)?.field;
      return { error: err.message, ...(field === "name" || field === "slug" ? { field } : {}) } satisfies ActionResult;
    }
    throw err;
  }
}

export function WorkspaceNewSkeleton() {
  const { t } = useI18n();
  return (
    <CenteredPage width={440}>
      <Loading>
        <div className="dt-centered__head">
          <div>
            <h1>{t("workspaceNew.title")}</h1>
            <p className="dt-muted">{t("workspaceNew.subtitle")}</p>
          </div>
        </div>
        <div className="dt-form">
          <FieldSkeleton label={t("workspaceNew.name")} />
          <FieldSkeleton label={t("workspaceNew.slug")} />
          <div className="dt-form__actions">
            <Skeleton shape="button" width={140} />
          </div>
        </div>
      </Loading>
    </CenteredPage>
  );
}

export function WorkspaceNewRoute() {
  const result = useActionData() as ActionResult | undefined;
  const nav = useNavigation();
  const { t } = useI18n();
  const toast = useToast();
  const location = useLocation();
  const back = backTarget(location.state);
  // The URL follows the name until the person edits it themselves — emptying it counts, or it would refill under their cursor.
  const [name, setName] = useState("");
  const [typedSlug, setTypedSlug] = useState<string | null>(null);
  const [fallback] = useState(() => `team-${Math.random().toString(36).slice(2, 8)}`);
  const slug = typedSlug ?? slugFor(name, fallback);
  // One toast per rejected submission. The ref survives StrictMode's double
  // effect, which would otherwise stack two identical toasts in development.
  const announced = useRef<ActionResult>(undefined);
  useEffect(() => {
    if (!result?.error || announced.current === result) return;
    announced.current = result;
    toast.danger(result.error, result.field === "slug" ? t("workspaceNew.slugTakenHint") : undefined);
  }, [result, toast, t]);
  return (
    <CenteredPage
      width={440}
      back={
        back && (
          <LinkButton to={back} variant="ghost" size="sm" icon={<ChevronLeftIcon />}>
            {t("common.back")}
          </LinkButton>
        )
      }
    >
      <div className="dt-centered__head">
        <div>
          <h1>{t("workspaceNew.title")}</h1>
          <p className="dt-muted">{t("workspaceNew.subtitle")}</p>
        </div>
      </div>
      {/* Carry the location state through a rejected submission, or the back button would vanish with it. */}
      <Form method="post" className="dt-form" state={location.state}>
        <TextField
          name="name"
          label={t("workspaceNew.name")}
          placeholder={t("workspaceNew.namePlaceholder")}
          error={result?.field === "name" ? result.error : null}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
        <TextField
          name="slug"
          label={t("workspaceNew.slug")}
          placeholder={t("workspaceNew.slugPlaceholder")}
          description={t("workspaceNew.slugHint")}
          error={result?.field === "slug" ? result.error : null}
          value={slug}
          onChange={(e) => setTypedSlug(e.target.value)}
          pattern="[a-z0-9]([a-z0-9-]*[a-z0-9])?"
          required
        />
        {/* Errors that name no field still need somewhere to land. */}
        {result?.error && !result.field && <FormError message={result.error} />}
        <div className="dt-form__actions">
          <Button type="submit" variant="primary" loading={nav.state === "submitting"}>
            {t("workspaceNew.submit")}
          </Button>
        </div>
      </Form>
    </CenteredPage>
  );
}
